import { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetSeatingTypeMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ seating_type_name: '', seating_type_code: '', is_active: true });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadRows = async () => {
    const res = await fetch('/api/v1/banquet/seating-types?all=1', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load seating types');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    loadRows().catch((err) => setError(err?.message || 'Failed to load seating types'));
  }, [chainId, propertyId, outletId]);

  const save = async () => {
    setError('');
    if (!form.seating_type_name) return setError('Seating type name is required');
    const url = editingId ? `/api/v1/banquet/seating-types/${editingId}` : '/api/v1/banquet/seating-types';
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        seating_type_name: form.seating_type_name,
        seating_type_code: form.seating_type_code || form.seating_type_name,
        is_active: form.is_active !== false
      })
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Save failed');
    setEditingId('');
    setForm({ seating_type_name: '', seating_type_code: '', is_active: true });
    loadRows();
  };

  const edit = (r) => {
    setEditingId(String(r.seating_type_id));
    setForm({
      seating_type_name: r.seating_type_name || '',
      seating_type_code: r.seating_type_code || '',
      is_active: r.is_active !== false
    });
    scrollToForm();
  };

  const deactivate = async (seatingTypeId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/seating-types/${seatingTypeId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Deactivate failed');
    loadRows();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Seating Type Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" ref={formRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <input placeholder="Seating type name (eg: Theater)" value={form.seating_type_name} onChange={(e) => setForm({ ...form, seating_type_name: e.target.value })} />
        <input placeholder="Code (optional, eg: THEATER)" value={form.seating_type_code} onChange={(e) => setForm({ ...form, seating_type_code: e.target.value })} />
        <label><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        <button onClick={save}>{editingId ? 'Update Seating Type' : 'Add Seating Type'}</button>
        {editingId ? <button onClick={() => { setEditingId(''); setForm({ seating_type_name: '', seating_type_code: '', is_active: true }); }}>Cancel</button> : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid var(--theme-border)' }}>
          <thead>
            <tr style={{ background: '#e6f0fa' }}>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'left' }}>Code</th>
              <th style={{ textAlign: 'left' }}>Active</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.seating_type_id}>
                <td>{r.seating_type_name}</td>
                <td>{r.seating_type_code}</td>
                <td>{r.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                  <button onClick={() => deactivate(r.seating_type_id)} style={{ marginLeft: '0.4rem' }}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={4}>No seating types found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetSeatingTypeMaster;
